import type { CapturedHttpResponse } from '@reqshare/protocol';
import { HeadersBlock } from '../HeadersBlock/HeadersBlock';
import { BodyBlock } from '../BodyBlock/BodyBlock';
import './ResponseBlock.scss';

interface ResponseBlockProps {
  response: CapturedHttpResponse;
}

function getStatusClass(status: number): string {
  if (status >= 200 && status < 300) return 'response-block__status--success';
  if (status >= 300 && status < 400) return 'response-block__status--redirect';
  if (status >= 400 && status < 500) return 'response-block__status--client-error';
  if (status >= 500) return 'response-block__status--server-error';
  return '';
}

function formatTiming(ms: number): string {
  if (ms < 0) return '-';
  if (ms < 1) return '<1 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

export function ResponseBlock({ response }: ResponseBlockProps) {
  return (
    <section className="response-block">
      <h2 className="response-block__heading">Response</h2>

      <div className="response-block__status-line">
        <span className={`response-block__status ${getStatusClass(response.status)}`}>
          {response.status}
        </span>
        <span className="response-block__status-text">{response.statusText}</span>
        <span className="response-block__http-version">{response.httpVersion}</span>
      </div>

      {response.timing && (
        <div className="response-block__timing">
          <h3 className="response-block__timing-title">Timing</h3>
          <div className="response-block__timing-grid">
            {[
              { label: 'Blocked', value: response.timing.blocked },
              { label: 'DNS', value: response.timing.dns },
              { label: 'Connect', value: response.timing.connect },
              { label: 'SSL', value: response.timing.ssl },
              { label: 'Send', value: response.timing.send },
              { label: 'Wait', value: response.timing.wait },
              { label: 'Receive', value: response.timing.receive },
              { label: 'Total', value: response.timing.total },
            ].map(({ label, value }) => (
              <div className="response-block__timing-item" key={label}>
                <span className="response-block__timing-label">{label}</span>
                <span className={`response-block__timing-value ${label === 'Total' ? 'response-block__timing-value--total' : ''}`}>
                  {formatTiming(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="response-block__sections">
        <HeadersBlock title="Response Headers" headers={response.headers} />
        <BodyBlock title="Response Body" body={response.body} />
      </div>
    </section>
  );
}
